import { Input, Tooltip } from "antd";
import * as React from "react";
import { IoArrowUp } from "react-icons/io5";

import { IAdvisorFreeTextSpec } from "@types";

import { AdvisorButton } from "./AdvisorButton";
import { cn } from "./cn";
import { Loader } from "./Loader";

const { TextArea } = Input;

/**
 * Live only for a turn that offers free text (only the opening node today, via `free_text`).
 * Every other turn's contract permits no free text — only a choice_id from the turn it just
 * sent — so leaving it enabled would offer an interaction the backend would reject.
 */
export function AdvisorComposer({
  freeText,
  draft,
  busy,
  onDraftChange,
  onSubmit,
}: {
  freeText: IAdvisorFreeTextSpec | null;
  draft: string;
  busy: boolean;
  onDraftChange: (value: string) => void;
  onSubmit: () => void;
}) {
  const disabled = !freeText || busy;
  const canSend = !disabled && draft.trim().length > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSubmit();
    }
  };

  return (
    <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-4 sm:px-6">
      <div
        className={cn(
          "flex items-end gap-2 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm",
          disabled && "cursor-not-allowed opacity-60"
        )}
      >
        <TextArea
          value={draft}
          onChange={e => onDraftChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            freeText?.placeholder ?? "Pick an option above to continue"
          }
          disabled={disabled}
          autoSize={{ minRows: 1, maxRows: 6 }}
          bordered={false}
          className="flex-1 resize-none"
        />
        {/*
          antd's Tooltip clones its direct child when that child is `disabled`, injecting an
          inline `display: inline-block` so the tooltip stays positionable — which silently wins
          over the button's own `flex` class and breaks the icon's centering. Giving Tooltip a
          plain (never-disabled) span to clone instead keeps the button's own layout untouched.
        */}
        <Tooltip title="Send">
          <span className="inline-flex shrink-0">
            <AdvisorButton
              variant="primary"
              size="icon"
              onClick={onSubmit}
              disabled={!canSend}
            >
              {busy ? (
                <Loader variant="circular" size="sm" />
              ) : (
                <IoArrowUp className="h-4 w-4" />
              )}
            </AdvisorButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}
